import { z } from 'zod';
import { zodEditStateEnum, editStateEnum } from './data-model.enums';
import { Sentences } from './data-model.sentences';
import { sentenceSchema } from './data-model.sentence';
import { CapicheLoggerCore } from '@capiche/ts-logger-core';

const logger: CapicheLoggerCore = new CapicheLoggerCore('paragraph:', false);

export const paragraphSchema = z.strictObject({
  // optional source ID for tracking
  srcId: z.string().optional(),
  // sequence ID for versioning
  seqId: z.number().int().min(0).optional(),
  // the paragraph text as provided by the user, including original case and any/all punctuation etc.
  input: z.string(),
  // the index of the paragraph in the input string
  index: z.number().int().min(0),
  // the length of the paragraph in the input string
  length: z.number().int().min(0),
  // the state of the paragraph in the editor
  // this state is used by the client to control the display of suggestions
  //  for example paragraphs that have been entirely replaced by the user should not show suggestions
  editState: zodEditStateEnum,

  // the spelling state of the sentence in the editor
  // the state refects the most severe state of any of the tokens in the sentence
  // misspelled > contextual > correct
  // spellingState: zodSpellingStateEnum, // TODO: this should be added

  // the sentences tokenized from input string
  // the backend processes and responds all the sentences
  sentences: z.array(sentenceSchema),
  // TODO: the corrected paragraph text as provided by the LLM, including original case and any/all punctuation etc.
  suggestion: z.string().optional(),
});

export type paragraphDTO = z.infer<typeof paragraphSchema>;

export class Paragraph {
  static dtoSchema = paragraphSchema;

  constructor(public readonly dto: paragraphDTO) {
    logger.log('constructor:', dto);
  }

  // Factory: from DTO
  static fromDTO(dto: paragraphDTO): Paragraph {
    // Validate at boundary
    Paragraph.dtoSchema.parse(dto);
    return new Paragraph(dto);
  }

  // Factory: from raw string
  static fromString(text: string, index: number = 0, seqId?: number, srcId?: string): Paragraph {
    logger.log('fromString:', text, seqId, srcId);
    // Sentence indices are paragraph-local (char offset within this paragraph's text).
    // Do NOT pass `index` (the paragraph's document-global offset) as `from` — that
    // would make sentence.index document-global, breaking both the server (which expects
    // paragraph-local indices) and the replacement formula para.index + sent.index + tok.index.
    const sentences: Sentences = Sentences.fromString(text, 0, srcId, seqId);

    return new Paragraph({
      srcId,
      seqId,
      input: text,
      suggestion: '', // initially empty, set by LLM later
      index,
      length: text.length,
      editState: editStateEnum.new,
      sentences: sentences.toDTOs(),
    });
  }

  // Serialization
  toDTO(): paragraphDTO {
    return this.dto;
  }

  // Business logic
  // called when the user make a change to the input field (type/paste/delete etc..)
  handleUserInput(input: string): Paragraph {
    // TODO: should this be passsed in from the parent so that a particualr seqId represnts the last usr input that an element was based on?
    logger.log('handleUserInput:', this.dto, input);

    const inputSeqId: number = (this.dto.seqId ?? 0) + 1;
    const inputSentences: Sentences = Sentences.fromString(
      input,
      0, // paragraph-local: sentence indices are relative to paragraph text, not document
      this.dto.srcId,
      inputSeqId,
    );

    // somehow compare inputSentences with this.dto.sentences
    // for each sentence: if there are no changes, use this.dto.sentence
    // if there are changes, ... call handleUserInput on the sentence??
    // build outputSentences from this logic
    const outputSentences: Sentences = inputSentences;

    return new Paragraph({
      ...this.dto,
      seqId: inputSeqId,
      input: input,
      length: input.length,
      editState: editStateEnum.pending, // mark as needing reprocessing
      sentences: outputSentences.toDTOs(),
    });
  }

  // called when capiche responds with suggestions
  handleCapicheResponse(response: paragraphDTO): Paragraph {
    const existingParagraph: Paragraph = this;
    const responseParagraph: Paragraph = Paragraph.fromDTO(response);

    // Reject if paragraph is from another source or stale
    if (responseParagraph.dto.srcId !== existingParagraph.dto.srcId) return existingParagraph;
    if ((responseParagraph.dto.seqId ?? 0) < (existingParagraph.dto.seqId ?? 0)) return existingParagraph;

    const existingSentences = existingParagraph.getSentences();
    const incomingSentences = responseParagraph.getSentences();

    const mergedSentences = existingSentences.map((existing) => {
      const incoming = incomingSentences.sentences.find((s) => s.dto.index === existing.dto.index);
      return incoming ? existing.handleCapicheResponse(incoming.toDTO()) : existing;
    });

    return new Paragraph({
      ...existingParagraph.dto,
      suggestion: response.suggestion ?? existingParagraph.dto.suggestion,
      editState: editStateEnum.checked,
      sentences: mergedSentences.toDTOs(),
    });
  }

  // sentence handling
  getSentences(): Sentences {
    return Sentences.fromDTOs(this.dto.sentences);
  }

  getSentenceCount(): number {
    return this.dto.sentences.length;
  }

  getTotalTokenCount(): number {
    return this.getSentences().getTotalTokenCount();
  }

  hasCorrectedSentences(): boolean {
    return this.getSentences().getCorrectedSentences().sentences.length > 0;
  }

  hasMisspelledTokens(): boolean {
    return this.getSentences().getTotalMisspelledTokenCount() > 0;
  }

  // Business logic: correction and state
  isCorrected(): boolean {
    return !!this.dto.suggestion && this.dto.input !== this.dto.suggestion;
  }

  withSuggestedText(suggested: string): Paragraph {
    return new Paragraph({
      ...this.dto,
      suggestion: suggested,
    });
  }

  withState(state: editStateEnum): Paragraph {
    return new Paragraph({
      ...this.dto,
      editState: state,
    });
  }

  withSentences(sentences: Sentences): Paragraph {
    return new Paragraph({
      ...this.dto,
      sentences: sentences.toDTOs(),
    });
  }
}
